// hooks/useAudioGeneration.js
import { useState } from 'react';
import axios from 'axios';
import { convertToSSML, getLanguageFromVoiceId } from '../ItineraryCreator/utils/ssmlUtils';
import { AVAILABLE_VOICES } from '../ItineraryCreator/utils/voiceConstants';
import { getFirebaseAuthToken } from '../../utils/firebaseAuthToken';
import { getBaseFilename } from '../ItineraryCreator/utils/filenameUtils';

export const useAudioGeneration = (defaultLanguage = 'en') => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const getDefaultChunkSizeForVoice = (voiceId) => {
    if (!voiceId) return 800;
    // Gemini 3.8 stays stable on long audio, so only very long texts get split
    if (voiceId.includes('SimpleTTS-38')) return 5000;
    if (voiceId.includes('Erinome')) return 600;
    if (voiceId.includes('SimpleTTS-Legacy') || voiceId.includes('SimpleTTS-Pro')) return 1000;
    if (voiceId.includes('SimpleTTS')) return 800;
    return 800;
  };

  const splitTextIntoChunks = (text, maxChunkSize = 800) => {
    if (!text || text.length <= maxChunkSize) return [text];

    const normalized = text.replace(/\r\n/g, '\n');
    const paragraphs = normalized.split('\n\n').map(p => p.trim()).filter(Boolean);

    if (paragraphs.length <= 1) {
      const chunks = [];
      let remaining = text.trim();
      while (remaining.length > maxChunkSize) {
        const searchText = remaining.substring(0, maxChunkSize);
        let lastSentenceEnd = -1;
        for (const match of searchText.matchAll(/[.!?]\s+(?=[A-Z])/g)) {
          if (match.index > maxChunkSize * 0.4) lastSentenceEnd = match.index + match[0].length;
        }
        const splitPoint = lastSentenceEnd > 0 ? lastSentenceEnd : (searchText.lastIndexOf(' ') + 1 || maxChunkSize);
        chunks.push(remaining.substring(0, splitPoint).trim());
        remaining = remaining.substring(splitPoint).trim();
      }
      if (remaining) chunks.push(remaining);
      return chunks;
    }

    const chunks = [];
    let currentChunk = '';
    for (const paragraph of paragraphs) {
      const wouldBe = currentChunk ? `${currentChunk}\n\n${paragraph}` : paragraph;
      if (currentChunk && wouldBe.length > maxChunkSize) {
        chunks.push(currentChunk);
        currentChunk = paragraph;
      } else {
        currentChunk = wouldBe;
      }
    }
    if (currentChunk) chunks.push(currentChunk);
    return chunks;
  };

  const generatePoiAudio = async (text, poi, itineraryMetadata, voiceId = 'en-US-Gemini-Aoede-SimpleTTS') => {
    try {
      setIsGenerating(true);
      setError(null);
      
      // Extract the language code from the voice ID using the utility function
      const languageCode = getLanguageFromVoiceId(voiceId);
      const shortLanguageCode = languageCode.split('-')[0]; // 'en', 'pt', etc.

      // Check if this is a Gemini voice
      const voiceConfig = AVAILABLE_VOICES[voiceId];
      const isGeminiVoice = voiceConfig?.provider === 'Gemini';
      const useGeminiTts = voiceConfig?.useGeminiTts || false;
      const useGeminiApi = voiceConfig?.useGeminiApi || false;

      // Only use SSML if enabled, using the centralized utility function
      const useSSML = poi.useSSML !== undefined ? poi.useSSML : true;
      const finalText = useSSML ? convertToSSML(text, languageCode) : text;

      console.log('Generating audio for:', {
        text: finalText.substring(0, 100) + '...',
        useSSML,
        language: shortLanguageCode,
        voiceId,
        isGeminiVoice,
        useGeminiTts,
        useGeminiApi,
        poiName: poi.name
      });

      // Sanitize country name to ensure no spaces in storage paths (use hyphens like backend)
      const sanitizedCountry = (itineraryMetadata.country || 'Unknown')
        .trim()
        .toLowerCase()
        .replace(/[^\w-]/g, '-');

      // Prepare request payload
      const requestPayload = {
        useSSML,
        languageCode: shortLanguageCode,
        isSpecial: true,
        voiceId: voiceId, // Send the selected voice ID
        storageConfig: {
          country: sanitizedCountry,
          itineraryName: getBaseFilename(itineraryMetadata) || itineraryMetadata.name,
          poiName: poi.name,
          language: languageCode // Send the language code
        }
      };

      const defaultChunkSize = getDefaultChunkSizeForVoice(voiceId);
      const shouldChunk = finalText.length > defaultChunkSize;
      const chunks = shouldChunk ? splitTextIntoChunks(finalText, defaultChunkSize) : [finalText];
      requestPayload.chunkingEnabled = true;
      requestPayload.chunkSize = defaultChunkSize;

      if (chunks.length > 1) {
        requestPayload.chunks = chunks;
        requestPayload.isChunkedRequest = true;
      } else {
        requestPayload.text = finalText;
      }

      console.log('[useAudioGeneration] Chunking decision', {
        voiceId,
        textLength: finalText.length,
        chunkSize: defaultChunkSize,
        chunkCount: chunks.length,
        chunked: chunks.length > 1
      });

      // Add Gemini-specific parameters if using Gemini voice
      if (useGeminiApi) {
        requestPayload.useGeminiApi = true;
        // No prompt needed for Gemini API - it uses inline pause markers
      } else if (useGeminiTts) {
        requestPayload.useGeminiTts = true;
      }

      const token = await getFirebaseAuthToken();
      const response = await axios.post(
        'https://us-central1-guidesofia-access-control.cloudfunctions.net/proxyItineraryBuilderNewTtsRequest',
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.data || (!response.data.audioContent && !response.data.audioUrl)) {
        console.error('[useAudioGeneration] Invalid API response structure. Response data:', response.data);
        throw new Error('No audio content received from API');
      }

      const audioUrl = response.data.compressedAudioUrl || response.data.audioUrl;
      const originalAudioUrl = response.data.uncompressedAudioUrl
        || response.data.originalAudioUrl
        || response.data.audioUrl;

      return {
        audioContent: response.data.audioContent,
        audioUrl,
        compressedAudioUrl: audioUrl,
        originalAudioUrl,
        uncompressedAudioUrl: originalAudioUrl,
        audioDuration: response.data.audioDuration ?? 0
      };
    } catch (error) {
      console.error('[useAudioGeneration] Error in generatePoiAudio hook:', error);
      setError(`Failed to generate audio in hook: ${error.message}`);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  return { generatePoiAudio, isGenerating, error };
};
